if (!customElements.get('jersey-personalizer')) {
  customElements.define(
    'jersey-personalizer',
    class JerseyPersonalizer extends HTMLElement {
      get formId() {
        return this.getAttribute('form');
      }

      get form() {
        return this.formId ? document.getElementById(this.formId) : null;
      }

      get toggle() {
        return this.querySelector('[data-jp-toggle]');
      }

      get feeInput() {
        return this.querySelector('[data-jp-fee]');
      }

      get feeVariantMarker() {
        return this.querySelector('[data-jp-fee-marker]');
      }

      get nameFieldMarker() {
        return this.querySelector('[data-jp-name-marker]');
      }

      // The font_size setting is authored in the theme editor at desktop width, where the
      // drawer caps at `.x-modal .drawer__inner { max-width: 60rem }` (see theme.css). Below
      // that width the drawer shrinks to the viewport, so a fixed px name looks oversized
      // relative to the jersey art on mobile. Scaling by the preview's actual rendered width
      // keeps the name the same size relative to the art on every screen.
      static previewReferenceWidth = 960;

      connectedCallback() {
        // x-modal reparents itself to document.body when it opens, so cache
        // references to the elements it contains now — live querySelector
        // calls on `this` would stop finding them once that happens.
        this.nameInput = this.querySelector('[data-jp-name-input]');
        this.preview = this.querySelector('[data-jp-preview]');
        this.previewContainer = this.querySelector('[data-jp-preview-container]');
        this.counter = this.querySelector('[data-jp-counter]');
        this.error = this.querySelector('[data-jp-error]');
        this.nameDisplay = this.querySelector('[data-jp-name-display]');
        this.confirmButton = this.querySelector('[data-jp-confirm]');
        this.modal = this.querySelector('x-modal');

        this.toggle?.addEventListener('change', this.onToggleChange.bind(this));
        this.nameInput?.addEventListener('input', this.onNameInput.bind(this));
        this.confirmButton?.addEventListener('click', this.onConfirmClick.bind(this));
        this.modal?.addEventListener('modal:afterHide', this.onModalAfterHide.bind(this));
        this.form?.addEventListener('submit', this.onFormSubmit.bind(this), true);

        if (this.previewContainer) {
          this.resizeObserver = new ResizeObserver((entries) => this.onPreviewResize(entries));
          this.resizeObserver.observe(this.previewContainer);
        }

        this.updateCounter();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      onPreviewResize(entries) {
        const width = entries[0]?.contentRect?.width;
        if (!width || !this.preview) return;

        const designFontSize = parseFloat(this.preview.getAttribute('data-jp-font-size'));
        if (!designFontSize) return;

        const scale = Math.min(width / this.constructor.previewReferenceWidth, 1);
        this.preview.style.fontSize = `${designFontSize * scale}px`;
      }

      onToggleChange(event) {
        if (event.target.checked) {
          this.modal?.show(this.toggle);
        } else {
          this.modal?.hide();
          this.resetPersonalization();
        }
      }

      // Jersey printing only supports Latin characters, so strip anything else
      // (Arabic, other scripts, symbols) as the user types.
      static allowedNamePattern = /[^A-Za-z0-9 '-]/g;

      onNameInput(event) {
        const before = event.target.value;
        const name = before.replace(this.constructor.allowedNamePattern, '');

        if (name !== before) event.target.value = name;

        this.setInvalid(name !== before);

        if (this.preview) this.preview.textContent = name;
        if (this.nameDisplay) this.nameDisplay.textContent = name;

        const isActive = !!(this.toggle?.checked && name.trim().length > 0);
        if (this.feeInput) this.feeInput.checked = isActive;
        if (this.feeVariantMarker) this.feeVariantMarker.disabled = !isActive;
        if (this.nameFieldMarker) this.nameFieldMarker.disabled = !isActive;

        this.updateCounter();
      }

      onConfirmClick() {
        if (!this.nameInput?.value.trim()) {
          this.nameInput?.focus();
          return;
        }

        this.modal?.hide();
      }

      onModalAfterHide() {
        if (this.nameInput?.value.trim()) return;

        if (this.toggle) this.toggle.checked = false;
        this.resetPersonalization();
      }

      resetPersonalization() {
        if (this.nameInput) this.nameInput.value = '';
        if (this.feeInput) this.feeInput.checked = false;
        if (this.feeVariantMarker) this.feeVariantMarker.disabled = true;
        if (this.nameFieldMarker) this.nameFieldMarker.disabled = true;
        if (this.preview) this.preview.textContent = '';
        if (this.nameDisplay) this.nameDisplay.textContent = '';
        this.setInvalid(false);
        this.updateCounter();
      }

      setInvalid(isInvalid) {
        this.nameInput?.classList.toggle('is-invalid', isInvalid);
        this.error?.classList.toggle('is-visible', isInvalid);
      }

      updateCounter() {
        if (!this.counter) return;

        const max = parseInt(this.counter.getAttribute('data-jp-max'), 10) || 0;
        const used = this.nameInput?.value.length || 0;
        this.counter.textContent = `${Math.max(max - used, 0)} characters left`;
      }

      onFormSubmit(event) {
        if (!this.toggle?.checked) return;
        if (this.nameInput?.value.trim()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.modal?.show(this.toggle);
        this.nameInput?.focus();
      }
    }
  );
}
